import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from '../hooks/useTranslation';
import { businessInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Submit to backend API
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 201) {
        toast({
          title: t.contact.formSuccess,
          description: t.contact.formSuccessDesc,
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-2 bg-burgundy-100 text-burgundy-700 rounded-full text-sm font-semibold mb-4">
            {t.contact.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-6">
            {t.contact.title}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t.contact.description}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-navy-900 mb-6">
                  {t.contact.infoTitle}
                </h3>
                <div className="space-y-6">
                  {/* Email */}
                  <div className="flex items-start gap-4 p-4 bg-navy-50 rounded-xl hover:bg-navy-100 transition-colors">
                    <div className="w-12 h-12 bg-burgundy-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1">{t.contact.email}</p>
                      <a 
                        href={`mailto:${businessInfo.email}`}
                        className="text-gray-600 hover:text-burgundy-700 transition-colors"
                      >
                        {businessInfo.email}
                      </a>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-4 p-4 bg-navy-50 rounded-xl hover:bg-navy-100 transition-colors">
                    <div className="w-12 h-12 bg-burgundy-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1">{t.contact.phone}</p>
                      <a 
                        href={`tel:${businessInfo.phone}`}
                        className="text-gray-600 hover:text-burgundy-700 transition-colors"
                      >
                        {businessInfo.phone}
                      </a>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-4 p-4 bg-navy-50 rounded-xl hover:bg-navy-100 transition-colors">
                    <div className="w-12 h-12 bg-burgundy-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1">{t.contact.address}</p>
                      <p className="text-gray-600">{businessInfo.fullAddress}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-gradient-to-br from-navy-900 to-navy-800 rounded-2xl p-8 text-white">
                <h4 className="text-xl font-bold mb-4">{t.contact.businessHours}</h4>
                <div className="space-y-2 text-white/90">
                  <div className="flex justify-between">
                    <span>{t.contact.monday}</span>
                    <span className="font-semibold">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t.contact.saturday}</span>
                    <span className="font-semibold">{t.contact.closed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t.contact.sunday}</span>
                    <span className="font-semibold">{t.contact.closed}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200">
              <h3 className="text-2xl font-bold text-navy-900 mb-6">
                {t.contact.formTitle}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-navy-900 mb-2">
                    {t.contact.formName} *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t.contact.formNamePlaceholder}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-navy-900 mb-2">
                    {t.contact.formEmail} *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.contact.formEmailPlaceholder}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-navy-900 mb-2">
                    {t.contact.formPhone}
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t.contact.formPhonePlaceholder}
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-navy-900 mb-2">
                    {t.contact.formMessage} *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t.contact.formMessagePlaceholder}
                    rows={5}
                    className="w-full resize-none"
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-burgundy-700 hover:bg-burgundy-800 text-white py-6 text-lg group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t.common.loading : t.contact.formSubmit}
                  {!isSubmitting && <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
